import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  TextField,
  FormControl,
  Checkbox,
  Grid,
  useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { StyledSelect, styledSelectMenuProps } from '../../../../common/StyledFormInputs';
import {
  PartnerSpecificFormConfig, PartnerSpecificFormData,
  PartnerSpecificFormInputValueType,
} from './PartnerSpecificFormConfig';
import { DialogCTAComponent } from './PartnerEnrollmentCardCommon';
import { PartnerName } from '../../../../common/constants';

interface PartnerSpecificFormDialogProps {
  config: PartnerSpecificFormConfig;
  onSubmitForm: (data: PartnerSpecificFormData | null) => void;
  loading: boolean;
  submitBtnTestId: string;
  closeBtnTestId: string;
  manufacturerName?: PartnerName;
}

export default function PartnerSpecificForm({
  config,
  onSubmitForm,
  loading,
  submitBtnTestId,
  closeBtnTestId,
  manufacturerName,
}: PartnerSpecificFormDialogProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const defaultValues = config?.questions?.reduce((acc, q) => {
    if (q.defaultValue !== undefined) {
      acc[q.key] = q.defaultValue;
    } else {
      acc[q.key] = q.type === 'multi-select' ? [] : '';
    }
    return acc;
  }, {} as Record<string, PartnerSpecificFormInputValueType>) || {};

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<Record<string, PartnerSpecificFormInputValueType>>({
    defaultValues,
    mode: 'onChange', // Validate on every change
  });

  const InstructionsSection = () => (
    <Box
      sx={{
        display: 'flex',
        padding: theme.spacing(2),
        gap: theme.spacing(2),
        borderRadius: theme.spacing(1.5),
        border: `1px solid ${theme.palette.grey[300]}`,
        boxShadow: '0 1px 2px 0 rgba(10, 13, 18, 0.05)',
      }}
    >
      <InfoOutlinedIcon style={{
        color: theme.palette.grey[800],
      }} />
      <Box>
        <Typography variant='h6' color={theme.palette.grey[800]}>
          {config.instructions}
        </Typography>

      </Box>
    </Box>
  );

  const onSubmit: SubmitHandler<Record<string, PartnerSpecificFormInputValueType>> = (data) => {
    /**
     * Multi-select question will results in array values for answers.
     * Since the backend accepts only strings,
     * convert any array-type answers to a single comma-separated string.
     */
    const normalizedData: Record<string, PartnerSpecificFormInputValueType> = {};
    for (const [key, value] of Object.entries(data)) {
      if (Array.isArray(value)) {
        normalizedData[key] = value.join(', ');
      } else {
        normalizedData[key] = value;
      }
    }

    const answers = config.questions?.reduce((acc, q) => {
      acc.questions[q.key] = {
        title: q.title,
        answer: normalizedData[q.key],
      };
      return acc;
    }, { version: config.version, questions: {} } as PartnerSpecificFormData);
    onSubmitForm(answers);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box display="flex" flexDirection="column" gap={3} mt={2}>
        {config?.questions?.map((q) => (
          <FormControl key={q.key} fullWidth required={q.required}>
            {q.type !== 'checkbox' && <Typography variant="subtitle1" gutterBottom>
              {q.label}
            </Typography>}
            <Controller
              name={q.key}
              control={control}
              rules={{
                required: q.required ? 'This field is required' : false,
                ...q.validation,
              }}
              render={({ field, fieldState }) => {
                switch (q.type) {
                  case 'radio':
                    return (
                      <RadioGroup
                        {...field}
                        value={field.value ?? ''}
                      >
                        <Grid container spacing={2}>
                          {q.options?.map((opt) => {
                            const isSelected = field.value === opt;
                            return (<Grid item xs={q.fullWidth ? 12 : 6} key={opt}>
                              <Box
                                sx={{
                                  border: `1px solid ${isSelected ?
                                    theme.palette.brand.primary :
                                    theme.palette.grey[300]}`,
                                  borderRadius: theme.spacing(1.25),
                                  padding: theme.spacing(1),
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <FormControlLabel
                                  value={opt}
                                  control={
                                    <Radio
                                      sx={{
                                        '&.Mui-checked': {
                                          color: theme.palette.brand.primary,
                                        },
                                        '&:hover': {
                                          backgroundColor: theme.palette.brand.surface,
                                        },
                                      }}
                                    />}
                                  label={opt}
                                  sx={{ margin: 0, width: '100%' }}
                                />
                              </Box>
                            </Grid>);
                          })}
                        </Grid>
                      </RadioGroup>
                    );
                  case 'select':
                    return (
                      <StyledSelect
                        {...field}
                        displayEmpty
                        value={field.value ?? ''}
                        MenuProps={styledSelectMenuProps}
                      >
                        {q.placeholder ?
                          <MenuItem disabled value="">
                            {q.placeholder}
                          </MenuItem> :
                          <MenuItem disabled value="">
                            {t('verification.defaultOptionsPlaceholder')}
                          </MenuItem>
                        }
                        {q.options?.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </StyledSelect>
                    );
                  case 'checkbox':
                    return (
                      <Grid item xs={12} sx={{
                        textAlign: 'start',
                        mt: theme.spacing(2.5),
                      }}>
                        <Box display={'flex'}>
                          <Checkbox
                            disableRipple
                            sx={{
                              paddingTop: theme.spacing(0),
                              paddingLeft: theme.spacing(0),
                              marginTop: theme.spacing(-0.5),
                              '&.Mui-checked': {
                                color: theme.palette.brand.primary,
                              },
                            }}
                            checked={field.value === 'Yes'}
                            onChange={(e) => (field.onChange(e.target.checked && 'Yes'))}
                          />
                          <Box display={'flex'} flexDirection={'column'}>
                            <Typography variant={'body7'} component={'div'}>{q.label}</Typography>
                            <Typography variant={'body8'} component={'div'}
                              sx={{
                                color: theme.palette.grey[700],
                              }}
                            >
                              {q.subLabel}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  case 'multi-select':
                    return (
                      <Grid container spacing={2}>
                        {q.options?.map((opt) => {
                          const isSelected = field.value.includes(opt);
                          return (<Grid item xs={12} key={opt}>
                            <Box
                              sx={{
                                border: `1px solid ${isSelected ?
                                  theme.palette.brand.primary :
                                  theme.palette.grey[300]}`,
                                borderRadius: theme.spacing(1.25),
                                padding: theme.spacing(1),
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <FormControlLabel
                                value={opt}
                                control={
                                  <Checkbox
                                    checked={field.value?.includes(opt)}
                                    onChange={(e) => {
                                      const currentValue =
                                        Array.isArray(field.value) ? field.value : [];

                                      const newValue = e.target.checked ?
                                        [...currentValue, opt] :
                                        currentValue.filter((v) => v !== opt);

                                      field.onChange(newValue);
                                    }}
                                    sx={{
                                      '&.Mui-checked': {
                                        color: theme.palette.brand.primary,
                                      },
                                      '&:hover': {
                                        backgroundColor: theme.palette.brand.surface,
                                      },
                                    }}
                                  />
                                }
                                label={opt}
                                sx={{ margin: 0, width: '100%' }}
                              />
                            </Box>
                          </Grid>);
                        })}
                      </Grid>
                    );
                  case 'text':
                    return (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter value"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    );
                  default:
                    return <></>;
                }
              }}
            />
          </FormControl>
        ))}

        {/* instruction section */}
        {config?.instructions && <InstructionsSection />}
        <DialogCTAComponent
          loading={loading}
          submitBtnTestId={submitBtnTestId}
          closeBtnTestId={closeBtnTestId}
          extraDisableCondition={!isValid}
          btnText={
            manufacturerName ?
              `${t('button.enrollAndContinueWith')} ${manufacturerName}` :
              (config?.submitCTAText || undefined)
          }
        />
      </Box>
    </form>
  );
}
